import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { cn } from '../../utils';

interface SideSheetProps {
  isOpen: boolean;
  headerChildren?: React.ReactNode;
  contentChildren?: React.ReactNode;
  footerChildren?: React.ReactNode;
  children?: React.ReactNode;
  onClose: () => void;
  closeOnClickOutside?: boolean;
  onClickOutside?: () => void;
  addOnClass?: string;
  /** Use when opening a side sheet above another overlay (e.g. bottom sheet at z-45). */
  elevated?: boolean;
}

const backdropVariants = {
  visible: { opacity: 1, transition: { duration: 0.1 } },
  hidden: { opacity: 0, transition: { duration: 0.3 } },
};

const sheetVariants = {
  visible: {
    x: '0%',
    transition: { type: 'spring', stiffness: 300, damping: 30 },
  },
  hidden: {
    x: '100%',
    transition: { type: 'spring', stiffness: 100, damping: 15, duration: 0.2 },
  },
};

/**
 * A customizable side sheet component for React applications.
 */
const SideSheet: React.FC<SideSheetProps> = ({
  isOpen,
  onClose,
  onClickOutside,
  closeOnClickOutside = true,
  headerChildren = null,
  contentChildren = null,
  footerChildren = null,
  children = null,
  addOnClass = '',
  elevated = false,
}) => {
  function handleClickOutside() {
    onClickOutside?.();
    if (closeOnClickOutside) {
      onClose();
    }
  }

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = '';
    }

    return () => {
      document.body.style.overflowY = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <motion.div
        className={cn(
          'fixed inset-0 bg-slate-900/40 backdrop-blur-xs',
          elevated ? 'z-[46]' : 'z-40',
        )}
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
        onClick={handleClickOutside}
      />
      <motion.div
        className={cn(
          'fixed inset-y-0 right-0 bg-white shadow-2xl h-screen w-full sm:w-[540px] md:w-[50vw] xl:w-[50vw] max-w-[840px] min-w-[360px] flex flex-col border-l border-slate-200/80',
          elevated ? 'z-[50]' : 'z-50',
          addOnClass,
        )}
        variants={sheetVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
        {/* Header */}
        {headerChildren}

        {/* Content Area */}
        {contentChildren ? (
          <div className="flex-1 overflow-y-auto bg-slate-50/30 p-0">{contentChildren}</div>
        ) : children ? (
          <div className="flex-1 overflow-y-auto bg-slate-50/30 p-0">{children}</div>
        ) : null}

        {/* Footer */}
        {footerChildren}
      </motion.div>
    </>
  );
};

export default SideSheet;
